<script setup lang="ts">
  import { computed, onMounted, ref, watch } from 'vue';
  import { isAxiosError } from 'axios';
  import { ColoredButton } from '@/shared/ui';
  import { fetchAllPages, isActiveUser, useEligibleUsersByPositions, useCustomToast } from '@/shared/lib';
  import { useAuthStore } from '@/entities/Session';
  import { positionApi } from '@/entities/Position';
  import { membersApi, type BranchMember } from '@/entities/Branch';
  import type { Position } from '@/entities/Position';
  import CheckMarkIcon from '@/shared/assets/icons/CheckMarkIcon.svg';
  import { attributionApi } from '../../api';
  import AdminPermissionsAssignmentSection from './AdminPermissionsAssignmentSection.vue';

  const toast = useCustomToast();

  const authStore = useAuthStore();
  const activeBranchId = computed(() => authStore.selectedBranchId);

  const positions = ref<Position[]>([]);
  const members = ref<BranchMember[]>([]);
  const membersLoaded = ref(false);
  const isSaving = ref(false);

  const selectedPositionsTraining = ref<string[]>([]);
  const selectedUsersTraining = ref<string[]>([]);
  const savedPositionsTraining = ref<string[]>([]);
  const savedUsersTraining = ref<string[]>([]);

  const selectedPositionsVerification = ref<string[]>([]);
  const selectedUsersVerification = ref<string[]>([]);
  const savedPositionsVerification = ref<string[]>([]);
  const savedUsersVerification = ref<string[]>([]);

  const sortedSelected = (arr: string[]) => [...arr].sort().join(',');

  const resetSelectionState = () => {
    selectedPositionsTraining.value = [];
    selectedUsersTraining.value = [];
    savedPositionsTraining.value = [];
    savedUsersTraining.value = [];
    selectedPositionsVerification.value = [];
    selectedUsersVerification.value = [];
    savedPositionsVerification.value = [];
    savedUsersVerification.value = [];
  };

  const isDirty = computed(
    () =>
      sortedSelected(selectedUsersTraining.value) !== sortedSelected(savedUsersTraining.value) ||
      sortedSelected(selectedUsersVerification.value) !== sortedSelected(savedUsersVerification.value)
  );

  const getMemberPositionIds = (member: BranchMember): number[] =>
    member.positions?.map((p) => p.id) ?? [];

  const getMemberDisplayName = (member: BranchMember) =>
    (member.user?.name?.trim() || member.user?.username?.trim() || member.user?.email) ?? '';

  const getMemberPositionName = (member: BranchMember) =>
    member.positions?.map((p) => p.name).join(', ') ?? '';

  // Categoria B: o pool nunca oferece um inativo novo — só o que já estava atribuído continua
  // visível (destacado em vermelho) após ficar inativo, para não ser removido silenciosamente.
  const trainingMemberPool = computed(() => {
    const attributedIds = new Set(savedUsersTraining.value);
    return members.value.filter(
      (member) => isActiveUser(member.user) || attributedIds.has(member.id?.toString() ?? '')
    );
  });

  const verificationMemberPool = computed(() => {
    const attributedIds = new Set(savedUsersVerification.value);
    return members.value.filter(
      (member) => isActiveUser(member.user) || attributedIds.has(member.id?.toString() ?? '')
    );
  });

  const { eligibleUsers: filteredTrainingMembers } = useEligibleUsersByPositions({
    users: trainingMemberPool,
    selectedPositions: selectedPositionsTraining,
    selectedUsers: selectedUsersTraining,
    getUserId: (member) => member.id,
    getUserPositionIds: getMemberPositionIds,
    clearSelectedUsersWhenNoPositions: false,
    canReconcile: membersLoaded,
  });

  const { eligibleUsers: filteredVerificationMembers } = useEligibleUsersByPositions({
    users: verificationMemberPool,
    selectedPositions: selectedPositionsVerification,
    selectedUsers: selectedUsersVerification,
    getUserId: (member) => member.id,
    getUserPositionIds: getMemberPositionIds,
    clearSelectedUsersWhenNoPositions: false,
    canReconcile: membersLoaded,
  });

  const derivePositionIds = (selectedUserIds: string[]): string[] => {
    const positionIds = new Set<string>();
    for (const member of members.value) {
      if (selectedUserIds.includes(member.id?.toString() ?? '')) {
        for (const posId of getMemberPositionIds(member)) {
          positionIds.add(posId.toString());
        }
      }
    }
    return Array.from(positionIds);
  };

  const toggleMember = (
    event: Event,
    selected: string[],
    setSelected: (value: string[]) => void,
    memberId: number | string | undefined
  ) => {
    if (memberId === undefined || memberId === null) return;
    const id = memberId.toString();
    const { checked } = event.target as HTMLInputElement;
    setSelected(checked ? [...selected, id] : selected.filter((v) => v !== id));
  };

  const loadPositions = async () => {
    try {
      positions.value = await fetchAllPages((page) =>
        positionApi.searchPositions({
          active: true,
          type: 'POSITION',
          page,
          limit: 100,
          sort: 'name',
          order: 'asc',
          branchId: activeBranchId.value ?? undefined,
        })
      );
    } catch {
      positions.value = [];
    }
  };

  const loadMembers = async () => {
    try {
      membersLoaded.value = false;
      members.value = await fetchAllPages((page) =>
        membersApi.getMembers(page, 100, undefined, 'name', 'asc', { includeInactiveMembers: true })
      );
    } catch {
      members.value = [];
    } finally {
      membersLoaded.value = true;
    }
  };

  const loadAttributions = async () => {
    try {
      const { training, verification } = await attributionApi.getAttributions();

      const trainingMemberIds = training.memberIds.map(String);
      const trainingPositionIds = training.positionIds.map(String);
      const verificationMemberIds = verification.memberIds.map(String);
      const verificationPositionIds = verification.positionIds.map(String);

      selectedPositionsTraining.value = trainingPositionIds;
      savedPositionsTraining.value = [...trainingPositionIds];
      selectedPositionsVerification.value = verificationPositionIds;
      savedPositionsVerification.value = [...verificationPositionIds];

      selectedUsersTraining.value = trainingMemberIds;
      savedUsersTraining.value = [...trainingMemberIds];
      selectedUsersVerification.value = verificationMemberIds;
      savedUsersVerification.value = [...verificationMemberIds];
    } catch {
      toast.showErrorToast({
        title: 'Erro ao carregar atribuições. Por favor, tente novamente.',
        type: 'error',
      });
    }
  };

  const handleSave = async () => {
    if (isSaving.value || !isDirty.value) return;
    isSaving.value = true;

    const trainingUserIds = [...selectedUsersTraining.value];
    const verificationUserIds = [...selectedUsersVerification.value];
    const trainingPositionIds = derivePositionIds(trainingUserIds);
    const verificationPositionIds = derivePositionIds(verificationUserIds);

    selectedPositionsTraining.value = trainingPositionIds;
    selectedPositionsVerification.value = verificationPositionIds;

    try {
      await attributionApi.updateAttributions({
        training: {
          memberIds: trainingUserIds.map(Number),
          positionIds: trainingPositionIds.map(Number),
        },
        verification: {
          memberIds: verificationUserIds.map(Number),
          positionIds: verificationPositionIds.map(Number),
        },
      });

      savedPositionsTraining.value = [...trainingPositionIds];
      savedUsersTraining.value = [...trainingUserIds];
      savedPositionsVerification.value = [...verificationPositionIds];
      savedUsersVerification.value = [...verificationUserIds];

      toast.showSuccessToast({ title: 'Atribuições salvas', type: 'success' });
    } catch (error) {
      const fallback = 'Erro ao salvar atribuições. Por favor, tente novamente.';
      const title =
        isAxiosError(error) && error.response?.data?.message ? error.response.data.message : fallback;
      toast.showErrorToast({ title, type: 'error' });
    } finally {
      isSaving.value = false;
    }
  };

  watch(activeBranchId, async () => {
    await Promise.all([loadPositions(), loadMembers()]);
    resetSelectionState();
    await loadAttributions();
  });

  onMounted(async () => {
    await Promise.all([loadPositions(), loadMembers(), loadAttributions()]);
  });
</script>

<template>
  <div class="mt-6">
    <AdminPermissionsAssignmentSection
      section-key="training"
      :description="'Usuários do RH que recebem e-mail de treinamento'"
      description-test-id="training-assignment-section-title"
      :positions="positions"
      :members="filteredTrainingMembers"
      :selected-positions="selectedPositionsTraining"
      :selected-users="selectedUsersTraining"
      @toggle="toggleMember"
      @update:selected-positions="selectedPositionsTraining = $event"
      @update:selected-users="selectedUsersTraining = $event"
    />

    <div
      data-testid="assignments-section-divider"
      class="border-b border-gray-02 my-8"
    />

    <AdminPermissionsAssignmentSection
      section-key="verification"
      :description="`Usuários da equipe de qualidade que receberão e-mail de notificação quando houver novos documentos pendentes de verificação`"
      description-test-id="verification-assignment-section-title"
      :positions="positions"
      :members="filteredVerificationMembers"
      :selected-positions="selectedPositionsVerification"
      :selected-users="selectedUsersVerification"
      @toggle="toggleMember"
      @update:selected-positions="selectedPositionsVerification = $event"
      @update:selected-users="selectedUsersVerification = $event"
    />

    <div
      data-testid="assignments-save-divider"
      class="border-b border-gray-02 mt-8"
    />

    <div class="flex justify-end gap-3 pt-6 pb-2">
      <ColoredButton
        text="Salvar"
        :icon="CheckMarkIcon"
        :outlined="false"
        :disabled="!isDirty || isSaving"
        @click="handleSave"
      />
    </div>
  </div>
</template>
